import { injectIntl, IntlShape } from 'react-intl';
import { translate } from 'containers/services/translate';
import { get, isEmpty } from 'lodash';
import { StyledRecapTitle } from 'containers/CourtManagerLegalProceedings/Provision/styled-components';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import {
  Title,
  Field,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import { Listings } from 'containers/AppContainer/typings';
import { Grid } from '@material-ui/core';
import {
  formatDate,
  getAdditionalAddress,
  getBeneficiaryRecipient,
  getCityByCode,
  getCityCodeByRisk,
  getPaymentNature,
  getProcessNature,
  getRealBeneficiary,
  getRegulationNatureTitle,
  getPaymentChannel,
  getPaymentMethod,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { RECIPIENT } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { RISK_NAME } from 'containers/CourtManagerLegalProceedings/Provision/constants';
import { IDD_CHECK } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';

export interface SideRegulationInformationProps {
  intl: IntlShape;
  risk: any;
  listings: Listings;
  substituteDocumentNature: any;
  showRegulationJudgement: boolean;
  isBasicManagementRegulation?: boolean;
}

export const SideRegulationInformation: React.FC<SideRegulationInformationProps> = ({
  risk,
  listings,
  substituteDocumentNature,
  showRegulationJudgement,
  ...props
}): JSX.Element => {
  return (
    <>
      <StyledRecapTitle>
        {translate(messages.judgementInformation, props)}
      </StyledRecapTitle>
      <Grid container spacing={1} direction="row">
        {risk.riskName !== RISK_NAME.GARANTIES_ANNEXES &&
          !props.isBasicManagementRegulation && (
            <Grid item xs={4}>
              <Title>{translate(messages.processNature, props)}</Title>
              <Field>{getProcessNature(listings, risk)}</Field>
            </Grid>
          )}
        {risk.riskName === RISK_NAME.CORPOREAL && (
          <Grid item xs={4}>
            <Title> {translate(messages.regulationNature, props)}</Title>
            <Field>
              {getRegulationNatureTitle(substituteDocumentNature, risk)}
            </Field>
          </Grid>
        )}

        <Grid item xs={4}>
          <Title>{translate(messages.regulationPhase, props)}</Title>
          <Field>
            {get(
              getPaymentNature(get(listings, 'PaymentNature', [])),
              'ItemDescription',
              '',
            )}
          </Field>
        </Grid>

        <Grid item xs={4}>
          <Title>{translate(messages.paymentMethod, props)}</Title>
          <Field>{getPaymentMethod(risk, listings)}</Field>
        </Grid>

        <Grid item xs={4}>
          <Title>{translate(messages.recipient, props)}</Title>
          <Field>
            {getBeneficiaryRecipient(
              risk,
              get(listings, 'EncryptDocumentCity', []),
            )}
          </Field>
        </Grid>
        <Grid item xs={4}>
          <Title>
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
            RECIPIENT.CDRA
              ? translate(messages.cdraRealBeneficiary, props)
              : translate(messages.realBeneficiary, props)}
          </Title>
          <Field>
            {getRealBeneficiary(risk, get(listings, 'EncryptDocumentCity', []))}
          </Field>
        </Grid>
        <Grid item xs={4}>
          <Title>{translate(messages.additionalAddress, props)}</Title>
          <Field>{getAdditionalAddress(risk)}</Field>
        </Grid>
        <Grid item xs={4}>
          <Title>{translate(messages.beneficiaryReference, props)}</Title>
          <Field>
            {get(risk.regulationInfo, [REGULATIONS.BENEFICIARY_REFERENCE], '')}
          </Field>
        </Grid>
        {get(risk, 'regulationInfo.beneficiaryRecipient', '') !==
          RECIPIENT.CDRA && (
          <>
            <Grid item xs={4}>
              <Title>{translate(messages.town, props)}</Title>
              <Field>{getCityByCode(risk, listings)}</Field>
            </Grid>
          </>
        )}
        {risk.riskName !== RISK_NAME.GARANTIES_ANNEXES && (
          <>
            {!isEmpty(
              get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE_JAF], ''),
            ) && (
              <>
                <Grid item xs={4}>
                  <Title>
                    <Title>{translate(messages.appealDate, props)}</Title>
                  </Title>
                  <Field>
                    {get(
                      risk.regulationInfo,
                      [REGULATIONS.JUDGEMENT_DATE],
                      null,
                    )}
                  </Field>
                </Grid>
                <Grid item xs={4}>
                  <Title>{translate(messages.judgementDate, props)}</Title>
                  <Field>
                    {get(
                      risk.regulationInfo,
                      [REGULATIONS.JUDGEMENT_DATE_JAF],
                      null,
                    )}
                  </Field>
                </Grid>
              </>
            )}
            {isEmpty(
              get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE_JAF], ''),
            ) && (
              <Grid item xs={4}>
                <Title>
                  {get(risk.regulationInfo, [REGULATIONS.IS_APPEAL], '')
                    ? translate(messages.appealDate, props)
                    : translate(messages.judgementDate, props)}
                </Title>
                <Field>
                  {get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null)}
                </Field>
              </Grid>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.courtNumber, props)}</Title>
              <Field>
                {get(risk.regulationInfo, [REGULATIONS.COURT_NUMBER], null)}
              </Field>
            </Grid>
          </>
        )}
        {risk.riskName === RISK_NAME.CORPOREAL && (
          <>
            {get(
              risk.regulationInfo,
              [REGULATIONS.BENEFICIARY_RECIPIENT],
              '',
            ) === RECIPIENT.BNEJ && (
              <>
                <Grid item xs={4}>
                  <Title> {translate(messages.bnejCity, props)}</Title>
                  <Field>{getCityCodeByRisk(risk, listings)}</Field>
                </Grid>
                <Grid item xs={4}>
                  <Title>{translate(messages.taxAmount, props)}</Title>
                  <Field>
                    {get(
                      risk.regulationInfo,
                      [REGULATIONS.BNEJ_TAX_AMOUNT],
                      '',
                    )}
                  </Field>
                </Grid>
              </>
            )}

            <Grid item xs={4}>
              <Title>{translate(messages.opposingLawyer, props)}</Title>
              <Field>
                {get(risk.regulationInfo, [REGULATIONS.OPPOSING_LAWYER], '')}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.regulationDate, props)}</Title>
              <Field>
                {formatDate(
                  get(risk.regulationInfo, [REGULATIONS.REGULATION_DATE]),
                ) !== 'Invalid date'
                  ? formatDate(
                      get(risk.regulationInfo, [REGULATIONS.REGULATION_DATE]),
                    )
                  : ''}
              </Field>
            </Grid>

            <Grid item xs={4}>
              <Title>{translate(messages.costs, props)}</Title>
              <Field>
                {get(
                  risk.regulationInfo,
                  [REGULATIONS.DIRECT_INDEMNITY_COSTS],
                  '',
                )}
              </Field>
            </Grid>

            <Grid item xs={4}>
              <Title>{translate(messages.iddField, props)}</Title>
              <Field>
                {get(risk.regulationInfo, [REGULATIONS.IS_IDD_CHECKED], '')}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.iddField, props)}</Title>
              <Field>
                {get(risk.regulationInfo, [REGULATIONS.IS_IDD_CHECKED], '')}
              </Field>
            </Grid>
            {get(risk.regulationInfo, [REGULATIONS.IS_IDD_CHECKED]) ===
              IDD_CHECK.TRUE && (
              <>
                <Grid item xs={4}>
                  <Title>{translate(messages.iddDate, props)}</Title>
                  <Field>
                    {formatDate(
                      get(
                        risk.regulationInfo,
                        [REGULATIONS.CALCULATION_START_DATE],
                        '',
                      ),
                    )}
                  </Field>
                </Grid>
                <Grid item xs={4}>
                  <Title>{translate(messages.iddDeduction, props)}</Title>
                  <Field>
                    {`${get(
                      risk.regulationInfo,
                      [REGULATIONS.DEDUCTION_RATE],
                      '',
                    )} %`}
                  </Field>
                </Grid>
              </>
            )}
          </>
        )}
        {get(risk.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '') ===
          RECIPIENT.OTHERS && (
          <Grid item xs={4}>
            <Title>{translate(messages.paymentChannel, props)}</Title>
            <Field>{getPaymentChannel(listings, risk)}</Field>
          </Grid>
        )}
        {risk.riskName !== RISK_NAME.CORPOREAL && (
          <Grid item xs={4}>
            <Title>{translate(messages.linkToBeneficiary, props)}</Title>
            <Field>
              {get(risk.regulationInfo, [REGULATIONS.LINK_TO_BENEFICIARY], '')}
            </Field>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default injectIntl(SideRegulationInformation);
